//codex-- Match the other redesigned toolbars while keeping the WIP report list's search/status controls.
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarDaysIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

//codex-- Preserve the original yellow/orange/purple WIP navigation colors in lighter shades.
const BUTTON_CLASS = 'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
const COLORS = {
  yellow: 'border-yellow-200 bg-yellow-100 text-yellow-900 hover:bg-yellow-200 focus-visible:outline-yellow-600',
  orange: 'border-orange-200 bg-orange-100 text-orange-900 hover:bg-orange-200 focus-visible:outline-orange-600',
  purple: 'border-purple-200 bg-purple-100 text-purple-900 hover:bg-purple-200 focus-visible:outline-purple-600',
  blue: 'border-blue-200 bg-blue-100 text-blue-900 hover:bg-blue-200 focus-visible:outline-blue-600',
};
const FIELD_CLASS = 'block h-11 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';
const EMPTY_FILTERS = { billing: 'all', search: '' };
const STATUS_OPTIONS = [
  ['all', 'All statuses'], ['invoiced', 'Invoiced'], ['notInvoiced', 'Not invoiced'],
  ['pending', 'Pending / active'], ['complete', 'Completed'], ['ready', 'Billable completed'],
];

//codex-- Draft edits stay local until Apply so typing does not rebuild the entire report list.
export default function WipListActionBar({ query, onQueryChange, onNewSchedule, loading }) {
  const [draft, setDraft] = useState(query);
  const previousQuery = useRef(query);
  useEffect(() => {
    const previous = previousQuery.current;
    const changed = Object.keys(EMPTY_FILTERS).filter((key) => previous[key] !== query[key]);
    if (changed.length) setDraft((current) => ({ ...current,
      ...Object.fromEntries(changed.filter((key) => current[key] === previous[key]).map((key) => [key, query[key]])),
    }));
    previousQuery.current = query;
  }, [query]);
  const hasChanges = draft.billing !== query.billing || draft.search.trim() !== query.search;

  //codex-- Submit the WIP status and search together, including native Enter submission.
  const applyFilters = (event) => {
    event.preventDefault();
    if (loading) return;
    const filters = { ...draft, search: draft.search.trim() };
    setDraft(filters);
    onQueryChange(filters);
  };

  //codex-- Clear filters through page state instead of reloading, preserving the selected month.
  const clearFilters = () => {
    setDraft({ ...EMPTY_FILTERS });
    onQueryChange({ ...EMPTY_FILTERS });
  };
  const removeFilter = (changes) => {
    setDraft((previous) => ({ ...previous, ...changes }));
    onQueryChange(changes);
  };

  return (
    //codex-- Put all WIP actions above labeled filters and keep navigation visible on mobile.
    <section aria-label="WIP report tools and filters" className="mb-6 mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <p className="text-sm font-semibold text-slate-800">WIP report tools</p>
        <nav aria-label="WIP report tools" className="flex flex-wrap gap-2">
          <NavLink to="/full_scheduler_viewer" className={`${BUTTON_CLASS} ${COLORS.yellow}`}>
            <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0" />Full scheduler
          </NavLink>
          <button type="button" onClick={onNewSchedule} className={`${BUTTON_CLASS} ${COLORS.orange}`}>
            <PlusIcon aria-hidden="true" className="h-4 w-4 shrink-0" />New schedule
          </button>
          <NavLink to="/wip_scheduler" className={`${BUTTON_CLASS} ${COLORS.purple}`}>
            <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0" />WIP calendar
          </NavLink>
        </nav>
      </div>

      {/*codex-- Give search enough space for job numbers and notes, with a separate status selector. */}
      <form onSubmit={applyFilters} className="p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <label htmlFor="wip-list-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search WIP schedules</label>
            <div className="relative">
              <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input id="wip-list-search" type="search" value={draft.search}
                onChange={(event) => setDraft((previous) => ({ ...previous, search: event.target.value }))}
                placeholder="Job number, site, customer, or notes" className={`${FIELD_CLASS} pl-9`} />
            </div>
          </div>
          <div>
            <label htmlFor="wip-list-status" className="mb-1.5 block text-xs font-semibold text-slate-600">WIP / billing status</label>
            <select id="wip-list-status" value={draft.billing} className={FIELD_CLASS}
              onChange={(event) => setDraft((previous) => ({ ...previous, billing: event.target.value }))}>
              {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        </div>
        {/*codex-- Explain the existing all-month search scope and the pending/applied distinction. */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-slate-500" role="status">
            {hasChanges ? 'Unapplied changes' : 'Search includes all months. Clear filters restores the selected month.'}
          </span>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <button type="button" onClick={clearFilters} className={`${BUTTON_CLASS} ${COLORS.orange}`}>
              <XMarkIcon aria-hidden="true" className="h-4 w-4" />Clear filters
            </button>
            <button type="submit" disabled={loading} className={`${BUTTON_CLASS} ${COLORS.blue} disabled:cursor-not-allowed disabled:opacity-50`}>
              Apply filters
            </button>
          </div>
        </div>
      </form>

      {/*codex-- Applied labels describe the shown results and can be removed individually. */}
      {(query.billing !== 'all' || query.search) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3 sm:px-5" aria-label="Applied filters">
          <span className="text-xs font-semibold text-slate-500">Applied</span>
          {query.billing !== 'all' && (
            <button type="button" aria-label="Remove status filter" onClick={() => removeFilter({ billing: 'all' })}
              className="inline-flex items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              {STATUS_OPTIONS.find(([value]) => value === query.billing)?.[1]}<XMarkIcon aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          )}
          {query.search && (
            <button type="button" aria-label="Remove search filter" onClick={() => removeFilter({ search: '' })}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <span className="max-w-[240px] truncate">Search: {query.search} (all months)</span><XMarkIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
